import { g as getRoomType } from './get-room-type.js';
import './index2.js';

/**
 * **Ascott Limited**
 *
 * @exports bs_374
 * @category engines
 */
function factory({ window, document, thn }) {
    const engine = {};
    /**
       * @memberOf module:bs_374
       * @see {@link prestart}
       *
       * @param {object} ctx - The context object.
       */
    engine.boot = function boot() {
        const dataLayer = window.dataLayer;
        if (dataLayer) {
            let dataLayerHelper = new thn.DataLayerHelper(window.dataLayer, function (_model, message) {
                if (message.PageType) {
                    setTimeout(thn.pageCheck, 800);
                }
            }, true);
            thn.shutdown(() => {
                dataLayerHelper.cancel();
                dataLayerHelper = null;
            });
        }
        const loadingContainer = document.querySelector('.stepper-box');
        if (loadingContainer) {
            const refreshPage = thn.debounce(() => {
                thn.pageCheck();
                if (thn.widgets.price.isTargetPage()) {
                    thn.widgets.price.refresh();
                }
            }, 1200);
            thn.observeDom(loadingContainer, refreshPage, {
                attributes: true,
            });
            thn.shutdown(() => {
                refreshPage.cancel();
                thn.stopObserveDom(loadingContainer, refreshPage);
            });
        }
    };
    /**
       * @memberOf module:bs_374
       * @this thn.widgets.price
       * @see {@link Price#observe_dom}
       *
       * @param {object} ctx - The context object.
       */
    engine.priceObserveDom = function priceObserveDom(ctx) {
        const container = document.querySelector('.main');
        if (container) {
            let refreshPrice = thn.debounce(() => thn.widgets.price.refresh(), 600);
            thn.observeDom(container, refreshPrice, {
                attributes: true,
                subtree: true,
                childList: true,
            });
            thn.pageUnload(() => {
                refreshPrice.cancel();
                thn.stopObserveDom(container, refreshPrice);
                refreshPrice = null;
            });
        }
    };
    /**
       * @memberOf module:bs_374
       * @see {@link is_user_logged}
       *
       * @param {object} ctx - The context object.
       */
    engine.isUserLogged = function isUserLogged(ctx) {
        const isLogged = !!(document.querySelector('[aria-label="User Icon"]'));
        return isLogged;
    };
    /**
     * @memberOf module:bs_374
     * @see {@link generate_search_url}
     *
     * @param {object} ctx - The context object.
     */
    engine.generateSearchUrl = function generateSearchUrl(ctx) {
        const url = new URL(ctx.baseUrl);
        const validUrl = url.pathname.match(/booking/);
        if (!validUrl) {
            ctx.newUrl = '';
            return ctx;
        }
        const hotelId = thn.objectGet(window.dataLayer, 'HotelID', { first: false });
        url.searchParams.set('arrive', '[date_begin]');
        url.searchParams.set('depart', '[date_end]');
        url.searchParams.set('adult', '[adults]');
        url.searchParams.set('child', '[children]');
        url.searchParams.set('hotel', hotelId);
        if (url.pathname.match(/^\/(\w{2}\/)/)) {
            url.pathname = url.pathname.replace(/^\/(\w{2}\/)/, '/[language]/');
        }
        ctx.newUrl = url.toString();
        ctx.newData.dateBegin = ctx.newData.dateBegin && ctx.newData.dateBegin?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3-$2-$1');
        ctx.newData.dateEnd = ctx.newData.dateEnd && ctx.newData.dateEnd?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3-$2-$1');
        return ctx;
    };
    /**
      *
      * @memberOf module:bs_371
      *
      */
    const roomDetailsParseData = (roomsArray = '') => [...roomsArray].map((room) => {
        const roomName = room.querySelector('.card-title')?.textContent;
        const roomType = getRoomType(roomName);
        const guestsElement = room.querySelector('.occupancy-description span');
        const guests = thn.parseNumber(guestsElement?.textContent?.charAt(0));
        const roomPrices = [...room.querySelectorAll('.asr-booking-rate-plan-item')].map((rateElement) => {
            const ratePrice = thn.parseNumber(rateElement.querySelector('.price-value')?.textContent);
            const rateName = rateElement.querySelector('.room-title')?.textContent?.trim();
            const nonRefundableEl = rateElement.querySelector('.asr-badge.payment-property.property-type');
            const isRefundable = !nonRefundableEl;
            return {
                id: '',
                name: rateName,
                totalNight: ratePrice,
                breakfast: 0,
                refundable: isRefundable,
            };
        });
        return {
            id: '',
            name: roomName,
            adults: guests,
            children: 0,
            type: roomType,
            prices: roomPrices,
        };
    });
    /**
       * @memberOf module:bs_374
       * @this thn.widgets.price
       * @see {@link Price#set_data}
       *
       * @param {object} ctx - The context object.
       */
    engine.priceParseData = function priceParseData(ctx) {
        const widgetData = {};
        let nights;
        const priceLoaderEl = document.querySelector('.loading');
        if (priceLoaderEl) {
            this.logger.debug('The price is still loading');
            return widgetData;
        }
        // Dates
        const dateBegin = thn.parseUrlParameter('arrive')?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$2/$1') || thn.dataLayerHelper.get('CheckinDate')?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$1/$2');
        const dateEnd = thn.parseUrlParameter('depart')?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$2/$1') || thn.dataLayerHelper.get('CheckoutDate')?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$1/$2');
        if (dateBegin && dateEnd) {
            widgetData.dateBegin = dateBegin;
            widgetData.dateEnd = dateEnd;
            nights = thn.daysBetween(widgetData.dateBegin, widgetData.dateEnd);
        }
        const roomsArray = document.querySelectorAll('#home .asr-booking-apartment-item');
        // Guests, Adults & Children
        const adultsDatalayer = thn.dataLayerHelper.get('TotalAdults');
        const childrenDataLayer = thn.dataLayerHelper.get('TotalChildren');
        widgetData.adults = adultsDatalayer || 2;
        widgetData.children = childrenDataLayer || 0;
        widgetData.guests = widgetData.adults + widgetData.children;
        // Price
        const priceList = [...document.querySelectorAll('.price-value')].filter((price) => thn.isVisible(price)).map((price) => thn.parseNumber(price?.textContent)).filter(Boolean);
        const pricePerStayRegex = /Per Stay|Par séjour|每住宿|Pro Aufenthalt|Por estancia|투숙(일) 당|1滞在あたり|Per Masa Inap|Mỗi lượt lưu trú/i;
        const pricePerStayText = document.querySelector('button.price-button > .toggle-text')?.textContent;
        const lowestPrice = Math.min(...priceList);
        const price = (typeof pricePerStayText === 'string' && pricePerStayText.match(pricePerStayRegex)) ? lowestPrice / nights : lowestPrice;
        // Currency
        const currencyElement = document.querySelector('.asr-top-select-currency .dropdown-text span');
        const currency = thn.parseCurrency(currencyElement?.textContent?.trim());
        // Check if the deal is selected and refresh the price
        if (document.querySelector('.deal.selected')) {
            widgetData.priceSpecial = 1;
        }
        if (price && currency) {
            widgetData.price = price;
            widgetData.currency = currency;
            widgetData.roomDetails = roomDetailsParseData(roomsArray);
            widgetData.set = true;
        }
        else if (!priceList.length && document.querySelector('.error-field')) {
            widgetData.noRooms = true;
        }
        return widgetData;
    };
    /**
       * @memberOf module:bs_374
       * @this thn.widgets.booking
       * @see {@link Booking#set_data}
       *
       * @param {object} ctx - The context object.
       */
    engine.bookingParseData = function bookingParseData(ctx) {
        const widgetData = {};
        const bookingIdElement = document.querySelector('.confirmation-number')?.textContent;
        let bookingId = bookingIdElement?.split(' ')?.[2];
        if (thn.data.language === 'de') {
            bookingId = bookingIdElement?.split(' ')?.[1];
        }
        if (!bookingId) {
            widgetData.set = false;
            return widgetData;
        }
        widgetData.bookingId = bookingId;
        widgetData.dateBegin = thn.dataLayerHelper.get('CheckinDate')?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$1/$2');
        widgetData.dateEnd = thn.dataLayerHelper.get('CheckoutDate')?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$1/$2');
        widgetData.adults = thn.dataLayerHelper.get('TotalAdults');
        widgetData.children = thn.dataLayerHelper.get('TotalChildren');
        widgetData.price = thn.parseNumber(thn.dataLayerHelper.get('DisplayRevenue'));
        widgetData.currency = thn.dataLayerHelper.get('DisplayCurrency');
        return widgetData;
    };
    return engine;
}

export { factory };
